// ============================================
// COMPONENT - CrearUsuarioModal
// ============================================

import { useState } from 'react';
import { MdClose } from 'react-icons/md';
import { useCrearUsuario, useSucursales, useUIStore } from '@almacenes/core';
import type { CrearUsuarioDto } from '@almacenes/core';

interface CrearUsuarioModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CrearUsuarioModal = ({ isOpen, onClose }: CrearUsuarioModalProps) => {
  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    sucursalId: '',
    password: '',
  });

  const { data: sucursales, isLoading: isLoadingSucursales } = useSucursales();
  const crearUsuarioMutation = useCrearUsuario();
  const addToast = useUIStore((state) => state.addToast);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validación
    if (!formData.nombre.trim()) {
      addToast('error', 'El nombre es obligatorio');
      return;
    }
    if (!formData.apellidos.trim()) {
      addToast('error', 'Los apellidos son obligatorios');
      return;
    }
    if (!formData.sucursalId) {
      addToast('error', 'Debe seleccionar una sucursal');
      return;
    }
    if (!formData.password.trim()) {
      addToast('error', 'La contraseña es obligatoria');
      return;
    }
    if (formData.password.length < 8) {
      addToast('error', 'La contraseña debe tener al menos 8 caracteres');
      return;
    }

    const datos: CrearUsuarioDto = {
      nombre: formData.nombre.trim(),
      apellidos: formData.apellidos.trim(),
      sucursalId: Number(formData.sucursalId),
      password: formData.password.trim(),
    };

    try {
      await crearUsuarioMutation.mutateAsync(datos);
      addToast('success', 'Usuario creado correctamente');
      setFormData({ nombre: '', apellidos: '', sucursalId: '', password: '' });
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Error al crear el usuario';
      addToast('error', errorMessage);
    }
  };

  const handleClose = () => {
    setFormData({ nombre: '', apellidos: '', sucursalId: '', password: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fadeIn">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
            Nuevo Usuario
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={crearUsuarioMutation.isPending}
          >
            <MdClose className="text-2xl" style={{ color: '#11194F' }} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Nombre */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent transition-all"
              placeholder="Ingrese el nombre"
              disabled={crearUsuarioMutation.isPending}
            />
          </div>

          {/* Apellidos */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
              Apellidos <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.apellidos}
              onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent transition-all"
              placeholder="Ingrese los apellidos"
              disabled={crearUsuarioMutation.isPending}
            />
          </div>

          {/* Sucursal */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
              Sucursal <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.sucursalId}
              onChange={(e) => setFormData({ ...formData, sucursalId: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent transition-all"
              disabled={crearUsuarioMutation.isPending || isLoadingSucursales}
            >
              <option value="">
                {isLoadingSucursales ? 'Cargando sucursales...' : 'Seleccione una sucursal'}
              </option>
              {sucursales
                ?.filter((s) => s.activo)
                .map((sucursal) => (
                  <option key={sucursal.id} value={sucursal.id}>
                    {sucursal.nombre} - {sucursal.direccion} #{sucursal.numeroDireccion}
                  </option>
                ))}
            </select>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
              Contraseña <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent transition-all"
              placeholder="Ingrese la contraseña (mínimo 8 caracteres)"
              disabled={crearUsuarioMutation.isPending}
              minLength={6}
            />
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              style={{ color: '#11194F' }}
              disabled={crearUsuarioMutation.isPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-[#43BFFF] text-white rounded-lg font-medium hover:bg-[#3AABEB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={crearUsuarioMutation.isPending}
            >
              {crearUsuarioMutation.isPending && (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              )}
              {crearUsuarioMutation.isPending ? 'Creando...' : 'Crear Usuario'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
